import _ from "lodash";
import React from "react";

import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import {
  AddChartIcon,
  CodeIcon,
  PinIcon,
} from "../components/icons/themes/materialdesign";
import {
  EditPropertyIcon,
  FontSizeIcon,
  LowercaseIcon,
  RemovePropertyIcon,
  RenameIcon,
  ReplaceIcon,
} from "../components/icons/themes/windows10";

import { DestinationKey } from "../destinations/types";
import { EventSource } from "../eventTypes";

export type TransformationOperationType =
  | "replaceName"
  | "replacePropertyKey"
  | "castPropertyValue"
  | "uppercasePropertyValue"
  | "lowercasePropertyValue"
  | "deleteProperty"
  | "sendBuiltinProperty"
  | "sendConstantProperty"
  | "sqlTransformation"
  | "postgresSQLTransformation";

const transformationOperations: TransformationOperationType[] = [
  "postgresSQLTransformation",
  "sqlTransformation",
  "replaceName",
  "replacePropertyKey",
  "castPropertyValue",
  "uppercasePropertyValue",
  "lowercasePropertyValue",
  "deleteProperty",
  "sendBuiltinProperty",
  "sendConstantProperty",
];

export const transformationOperationText: Record<
  TransformationOperationType,
  string | JSX.Element
> = {
  deleteProperty: "Delete Property",
  replaceName: "Rename Event",
  castPropertyValue: "Change Property Type",
  lowercasePropertyValue: "Lowercase Property Value",
  replacePropertyKey: "Rename Property",
  uppercasePropertyValue: "Uppercase Property Value",
  sendBuiltinProperty: "Send Built-in Property",
  sendConstantProperty: "Send Constant Property",
  sqlTransformation: "Custom SQL Transformation - Deprecated",
  postgresSQLTransformation: "Custom SQL Transformation",
};

type IconElement = React.ForwardRefExoticComponent<any>;
export const transformationOperationIcons: Record<
  TransformationOperationType,
  IconElement
> = {
  castPropertyValue: EditPropertyIcon,
  deleteProperty: RemovePropertyIcon,
  replaceName: RenameIcon,
  replacePropertyKey: ReplaceIcon,
  lowercasePropertyValue: FontSizeIcon,
  uppercasePropertyValue: LowercaseIcon,
  sendBuiltinProperty: AddChartIcon,
  sendConstantProperty: PinIcon,
  sqlTransformation: CodeIcon,
  postgresSQLTransformation: CodeIcon,
};

export type TransformOperationOptionType = {
  value: TransformationOperationType;
  label: string | JSX.Element;
  icon: React.ForwardRefExoticComponent<any>;
};

export function transformOperationOptions(
  canUseSQLTransformation: boolean,
  serverSideOnly: boolean,
  eventType: TransformationEventType,
): TransformOperationOptionType[] {
  return _(transformationOperations)
    .map((op) => {
      if (op === "sqlTransformation") {
        return null; // SingleStore SQL transformations are no longer supported for new creation
      } else if (op === "postgresSQLTransformation") {
        if (eventType === "identify") {
          return null; // we currently don't support SQL transforms for identify events
        }

        if (!canUseSQLTransformation) {
          return null;
        }

        const clientSideDisabledTooltip =
          "SQL Transformations can only be applied when either the event is a server-side event or the destination is in server-side mode.";

        const disabled = !serverSideOnly;

        var label = transformationOperationText[op];
        if (!serverSideOnly) {
          label = (
            <Tooltip
              content={clientSideDisabledTooltip}
              contentProps={{ side: "right" }}
            >
              <span>{label}</span>
            </Tooltip>
          );
        }

        return {
          value: op,
          label: label,
          icon: transformationOperationIcons[op],
          isDisabled: disabled,
        };
      } else if (op === "replaceName" && eventType === "identify") {
        return null; // returns a no-op bc replaceName operation doesn't make sense for identify events
      } else {
        return {
          value: op,
          label: transformationOperationText[op],
          icon: transformationOperationIcons[op],
        };
      }
    })
    .compact()
    .value();
}

export type TransformationIntention =
  | "fixBadData"
  | "standardEvent"
  | "identifyEvent";

// Added `hipaa_guard` as a valid TransformationEventType to be able to re-use
// the BeforeAfter diff tool in Traces.tsx
export type TransformationEventType =
  | "track"
  | "identify"
  | "hipaa_guard"
  | "select_relevant_properties";

export type LoadTransformationData = {
  ID: string | null;
  targetValue: string | null;
  operation: TransformationOperationType;
  operandValue: string | number | boolean | null;
  enabled: boolean;
  createdAt: string;
  priority: number;
};

export type LoadedTransformationSet = {
  id: string;
  envID: string;
  createdAt: string;
  createdByUserId: string;
  createdByUserEmail: string;
  destinationName: DestinationKey | "ALL_DESTINATIONS_VALUE" | null;
  eventDefId: string | null;
  eventDefName: string | null;
  eventDefEventSource: EventSource;
  description: string;
  intention: TransformationIntention;
  eventType: TransformationEventType;
  transformations: LoadTransformationData[];
  priority: number;
};

export type GetTransformationSetsResponse = LoadedTransformationSet[];

export const TypeSelectorOptions: { value: string; label: string }[] = [
  {
    value: "number",
    label: "Number",
  },
  {
    value: "boolean",
    label: "True/False",
  },
  {
    value: "string",
    label: "Text",
  },
];

export type TransformationPageFilter = {
  type: "destinationName" | "eventDefId" | "eventDefName" | "search";
  value: string | null;
};

export type TransformationPageSortColumns =
  | "destinationName"
  | "createdAt"
  | "eventDefName"
  | "intention";

export type TransformationPageSort = {
  column: TransformationPageSortColumns;
  direction: "asc" | "desc";
};
